import { useState, useEffect, useRef, useCallback } from "react";
import { fetchDepositQuote, fetchWithdrawQuote } from "./bridgeHelpers";

const QUOTE_DEBOUNCE_MS = 600;

/**
 * Hook for fetching deposit quotes via on-chain OFT quoteOFT + quoteSend.
 * Debounces by 600ms. Returns fee in source chain native token.
 */
export function useDepositQuote({ chain, amount, walletAddress }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const versionRef = useRef(0);

  const doFetch = useCallback(async () => {
    const version = ++versionRef.current;

    if (!chain || !amount || !walletAddress) {
      setQuote(null);
      setError(null);
      return;
    }

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setQuote(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchDepositQuote(chain, amount, walletAddress);
      if (version === versionRef.current) {
        setQuote(result);
        setLoading(false);
      }
    } catch (err) {
      console.error(`[Bridge] Deposit quote error for ${chain?.name}:`, err.message);
      if (version === versionRef.current) {
        setError(err.message || "Quote unavailable");
        setQuote(null);
        setLoading(false);
      }
    }
  }, [chain, amount, walletAddress]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      setQuote(null);
      setError(null);
      setLoading(false);
      versionRef.current++;
      return;
    }

    timerRef.current = setTimeout(doFetch, QUOTE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [doFetch, amount]);

  return { quote, loading, error };
}

/**
 * Hook for fetching withdraw quotes via on-chain OFT calls.
 * Returns fee in NIBI + min received amount.
 */
export function useWithdrawQuote({ destChain, amount, walletAddress }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const versionRef = useRef(0);

  const doFetch = useCallback(async () => {
    const version = ++versionRef.current;

    if (!destChain || !amount || !walletAddress) {
      setQuote(null);
      setError(null);
      return;
    }

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setQuote(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    console.log(`[Bridge] Fetching withdraw quote for ${amount} USDC to ${destChain.name}`);

    try {
      const result = await fetchWithdrawQuote(destChain, amount, walletAddress);
      if (version === versionRef.current) {
        console.log(`[Bridge] Withdraw quote result:`, result);
        setQuote(result);
        setLoading(false);
      }
    } catch (err) {
      if (version === versionRef.current) {
        console.error(`[Bridge] Withdraw quote error:`, err.message);
        setError(err.message || "Quote unavailable");
        setQuote(null);
        setLoading(false);
      }
    }
  }, [destChain, amount, walletAddress]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      setQuote(null);
      setError(null);
      setLoading(false);
      versionRef.current++;
      return;
    }

    timerRef.current = setTimeout(doFetch, QUOTE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [doFetch, amount]);

  return { quote, loading, error };
}
